import { memo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import type { Form } from "@/types/forms";

interface FormCardProps {
  form: Form;
  onEditClick: (form: Form) => void;
  onPublishClick: (form: Form) => void;
  onUnpublishClick: (form: Form) => void;
  onArchiveClick: (form: Form) => void;
  onRestoreClick: (form: Form) => void;
}

export const FormCard = memo(function FormCard({
  form,
  onEditClick,
  onPublishClick,
  onUnpublishClick,
  onArchiveClick,
  onRestoreClick,
}: FormCardProps) {
  const isDraft = form.status === "draft";
  const isPublished = form.status === "published";

  return (
    <div className={`flex items-center justify-between rounded-lg border p-4${form.isArchived ? " opacity-60" : ""}`}>
      <div>
        <p className="font-medium">{form.title}</p>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant={isPublished ? "default" : "secondary"}>
            {form.status}
          </Badge>
          {form.isArchived && (
            <Badge variant="outline">archived</Badge>
          )}
          {form.description && <span>{form.description}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isDraft && !form.isArchived && (
          <Button variant="outline" size="sm" onClick={() => onPublishClick(form)}>
            Publish
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => onEditClick(form)}>
          Edit
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isPublished && !form.isArchived && (
              <DropdownMenuItem onClick={() => onUnpublishClick(form)}>
                Unpublish
              </DropdownMenuItem>
            )}
            {form.isArchived && (
              <DropdownMenuItem onClick={() => onRestoreClick(form)}>
                Restore
              </DropdownMenuItem>
            )}
            {!form.isArchived && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onArchiveClick(form)}
              >
                Archive
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
});
